// useNodeRelatedGroups.js

import { useMemo } from 'react';

// Utilities
import { getPluralLabel } from 'utilities/Languages';
import {
  expandRelatedTypeOrder,
  listTabHiddenRelatedTypeKeys,
} from 'utilities/nodeTypeRelatedLists';

export default function useNodeRelatedGroups({
  childNodes = [],
  parentTypeDef = null,
  getType,
  language,
}) {
  return useMemo(() => {
    if (!Array.isArray(childNodes) || childNodes.length === 0) return [];

    const countsByType = new Map();
    const tabHiddenTypeKeys = new Set(
      listTabHiddenRelatedTypeKeys(parentTypeDef?.suggestedChildren),
    );

    childNodes.forEach((item) => {
      const typeKey = typeof item?.type === 'string' ? item.type.trim() : '';
      if (!typeKey || tabHiddenTypeKeys.has(typeKey)) return;
      countsByType.set(typeKey, (countsByType.get(typeKey) || 0) + 1);
    });

    if (!countsByType.size) return [];

    const typeOrder = expandRelatedTypeOrder(parentTypeDef?.suggestedChildren);
    const orderedTypeKeys = Array.from(countsByType.keys()).sort((a, b) => {
      const aIdx = typeOrder.indexOf(a);
      const bIdx = typeOrder.indexOf(b);
      const aPriority = aIdx === -1 ? Number.MAX_SAFE_INTEGER : aIdx;
      const bPriority = bIdx === -1 ? Number.MAX_SAFE_INTEGER : bIdx;
      if (aPriority === bPriority) return a.localeCompare(b);
      return aPriority - bPriority;
    });

    return orderedTypeKeys.map((typeKey) => {
      const typeDef = getType(typeKey);
      return {
        count: countsByType.get(typeKey) || 0,
        key: typeKey,
        label: getPluralLabel(typeDef, typeKey, language),
      };
    });
  }, [childNodes, getType, language, parentTypeDef?.suggestedChildren]);
}